// Turns a station's `offerings.fuelOptions` (maps/gas-stations-by-fuel) into
// what the station card and details sheet show: every reported fuel price,
// grouped by category, plus the fuels the station is known to sell but has
// no price for yet.

// Display order of the categories; unknown ones go last.
export const FUEL_CATEGORIES = ['petrol', 'diesel', 'lpg', 'cng'];

// Short chip labels for the card, by fuel `code`. Unknown codes fall back to
// the backend name.
const SHORT_NAMES = {
  euro95_e10: 'E10',
  euro95_e5: 'E5',
  superplus_98: '98',
  premium_100: '100',
  diesel_b7: 'Diesel',
  diesel_plus: 'Diesel+',
  hvo100: 'HVO100',
  lpg: 'LPG',
  cng: 'CNG',
};

// A price older than this is shown muted, so users can tell it may be off.
const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

const categoryRank = category => {
  const index = FUEL_CATEGORIES.indexOf(category);
  return index === -1 ? FUEL_CATEGORIES.length : index;
};

// "1.989", "3.80": pump prices often have three decimals, never fewer than two.
export const formatFuelPrice = price => {
  const value = typeof price === 'number' ? price : parseFloat(price);
  if (!Number.isFinite(value)) {
    return null;
  }
  return value.toFixed(3).replace(/0$/, '');
};

export const shortFuelName = fuel =>
  SHORT_NAMES[fuel?.code] || fuel?.name || fuel?.type || '';

// Milliseconds from an ISO date (or ms), or null.
export const toMs = value => {
  if (!value) {
    return null;
  }
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

export const isStalePrice = updatedAt => {
  const ms = toMs(updatedAt);
  return ms !== null && Date.now() - ms > STALE_AFTER_MS;
};

const isSameFuel = (fuel, selected) =>
  !!selected &&
  (selected.fuelOptionId != null
    ? fuel.fuelOptionId === selected.fuelOptionId
    : fuel.code === selected.code);

/**
 * @returns {{
 *   prices: Array,        priced fuels in category order, each with
 *                         `isSelected`
 *   groups: Array,        [{category, fuels}] of the same prices
 *   unpricedCategories: string[]  categories sold here without a price
 *   hasEv: boolean,
 *   evConnectors: number|null,
 * }}
 */
export const getFuelOfferings = station => {
  const options = station?.offerings?.fuelOptions;
  const selected = station?.selectedFuel;
  const raw = Array.isArray(options?.fuelPrices) ? options.fuelPrices : [];

  const prices = raw
    .filter(fuel => formatFuelPrice(fuel?.price) !== null)
    .map(fuel => ({...fuel, isSelected: isSameFuel(fuel, selected)}))
    .sort(
      (a, b) =>
        categoryRank(a.category) - categoryRank(b.category) ||
        (a.fuelOptionId ?? 0) - (b.fuelOptionId ?? 0),
    );

  const groups = [];
  prices.forEach(fuel => {
    const category = fuel.category || 'other';
    let group = groups.find(g => g.category === category);
    if (!group) {
      group = {category, fuels: []};
      groups.push(group);
    }
    group.fuels.push(fuel);
  });

  // `petrol: true` etc. without any price of that category.
  const pricedCategories = new Set(groups.map(g => g.category));
  const unpricedCategories = FUEL_CATEGORIES.filter(
    category => options?.[category] === true && !pricedCategories.has(category),
  );

  const ev = station?.offerings?.evCharging;
  const hasEv = !!(ev?.dataAvailable && ev.available);

  return {
    prices,
    groups,
    unpricedCategories,
    hasEv,
    evConnectors: hasEv && ev.connectorCount ? ev.connectorCount : null,
  };
};
